// Utilities for simulating network tests in a browser environment
export const measureLatency = async (url) => {
  const start = performance.now();
  try {
    await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      signal: AbortSignal.timeout(3000)
    });
    const end = performance.now();
    return Math.round(end - start);
  } catch (error) {
    return -1;
  }
};

/**
 * Simulates RF metrics based on connection quality.
 * Real RSRP/RSRQ/SINR are not accessible via standard Browser APIs,
 * so we model them based on latency, jitter, and connection type for testing.
 */
export const calculateRFMetrics = (ping, jitter, is5G) => {
  // RSRP: -44 (Excellent) to -140 (Dead). 
  let rsrp = is5G ? -80 : -95;
  if (ping > 100) rsrp -= 20;
  if (ping < 30) rsrp += 15;
  rsrp += Math.floor(Math.random() * 5) - 2; // Add minor fluctuation

  // RSRQ: -3 (Excellent) to -20 (Poor).
  let rsrq = is5G ? -8 : -12;
  if (jitter > 20) rsrq -= 4;
  if (jitter < 5) rsrq += 2;
  rsrq += Math.floor(Math.random() * 2) - 1;

  // SINR: 0 (Poor) to 30 (Excellent).
  let sinr = is5G ? 18 : 12;
  if (jitter > 15) sinr -= 5;
  if (ping < 25) sinr += 7;
  sinr = Math.min(30, Math.max(0, sinr + (Math.random() * 2 - 1)));

  return {
    rsrp: Math.round(rsrp),
    rsrq: Math.round(rsrq),
    sinr: parseFloat(sinr.toFixed(1))
  };
};

export const getStabilityScore = (latency, jitter, packetLoss) => {
  let score = 100;
  if (latency > 50) score -= 15;
  if (latency > 100) score -= 25;
  if (latency > 300) score -= 40;
  if (jitter > 10) score -= 15;
  if (jitter > 30) score -= 30;
  if (packetLoss > 0) score -= (packetLoss * 10);
  return Math.max(0, Math.round(score));
};

export const getConnectionType = () => {
  if (navigator.connection) {
    const { effectiveType, type, downlink } = navigator.connection;
    const isHighSpeedCellular = (type === 'cellular' || !type) && (effectiveType === '4g' || downlink > 10);
    return {
      type: isHighSpeedCellular ? '5G Mobile Data' : (type === 'wifi' ? 'Wi-Fi 6' : 'Ethernet/Broadband'),
      effective: isHighSpeedCellular ? '5G UW' : (effectiveType ? effectiveType.toUpperCase() : 'HIGH-SPEED'),
      is5G: isHighSpeedCellular,
      downlink: downlink || '---'
    };
  }
  return { type: '5G Mobile Data', effective: '5G UW', is5G: true, downlink: '---' };
};